<template>
  <div class="overview-pages ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">dashboard</i>
      <Tooltip :text="$t('dash.overview.help.pages.title')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.pages.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span class="ov-chip-muted">{{ pages.length }}</span>
      <button v-if="canEdit" class="ov-arrow-link ov-block-link" @click="$emit('go', { name: 'pages' })">{{ $t('dash.overview.pages.manage') }}<i class="material-icons">arrow_forward</i></button>
    </div>

    <p v-if="!pages.length" class="ov-empty">{{ $t('dash.overview.pages.empty') }}</p>

    <ul class="pages-list" v-else>
      <li v-for="page in sortedPages" :key="page.id" class="page-row">
        <div class="page-avatar"><page-image-avatar :page="page" /></div>
        <div class="page-main">
          <p class="page-name">{{ page.name }}</p>
          <div class="conn-badges">
            <Tooltip v-for="conn in connections(page)" :key="conn.type" :text="conn.tooltip" position="top">
              <span class="conn-badge" :class="`conn-badge--${conn.level}`">
                <span class="dot"></span>{{ conn.label }}
              </span>
            </Tooltip>
          </div>
        </div>

        <div class="page-stats">
          <Tooltip v-if="stat(page).page_fans !== null && stat(page).page_fans !== undefined" :text="$t('dash.overview.help.page.followers')" position="top" class="page-stat">
            <strong>{{ compact(stat(page).page_fans) }}</strong>
            <span v-if="stat(page).fans_delta" :class="stat(page).fans_delta > 0 ? 'up' : 'down'">
              {{ stat(page).fans_delta > 0 ? '+' : '' }}{{ compact(stat(page).fans_delta) }}
            </span>
            <small>{{ $t('dash.overview.pages.followers') }}</small>
          </Tooltip>
          <Tooltip v-if="statsLoaded" :text="$t('dash.overview.help.page.posted')" position="top" class="page-stat">
            <strong>{{ stat(page).posted || 0 }}</strong>
            <small>{{ $t('dash.overview.pages.posted') }}</small>
          </Tooltip>
          <Tooltip v-if="statsLoaded && stat(page).scheduled" :text="$t('dash.overview.help.page.scheduled')" position="top" class="page-stat">
            <strong>{{ stat(page).scheduled }}</strong>
            <small>{{ $t('dash.overview.pages.scheduled') }}</small>
          </Tooltip>
        </div>

        <div class="page-actions" v-if="canEdit">
          <!-- API : reconnexion OAuth (sur place si possible, sinon section Pages) -->
          <Tooltip v-if="apiNeedsReconnect(page)" :text="$t('dash.overview.pages.reconnect_api')" position="top">
            <button
              class="ov-icon-btn ov-icon-btn--danger"
              @click="canReconnectInline(page) ? $emit('reconnect', page) : $emit('go', { name: 'pages' })"
            ><i class="material-icons">sync</i></button>
          </Tooltip>
          <!-- Natif : même formulaire que la section Pages (identifiant / mot de passe Instagram, extension pour les autres) -->
          <Tooltip v-if="nativeNeedsReconnect(page)" :text="$t(`dash.overview.pages.reconnect_native_${page.network === 'instagram' ? 'login' : 'extension'}`)" position="top">
            <button class="ov-icon-btn ov-icon-btn--danger" @click="nativePage = page">
              <i class="material-icons">{{ page.network === 'instagram' ? 'vpn_key' : 'extension' }}</i>
            </button>
          </Tooltip>
          <!-- Page déconnectée : seule la reconnexion est proposée -->
          <template v-if="!isDisconnected(page)">
            <Tooltip :text="$t('dash.overview.actions.create_post')" position="top">
              <button class="ov-icon-btn" @click="$emit('create-post', page)"><i class="material-icons">add</i></button>
            </Tooltip>
            <Tooltip v-if="reportingAllowed && isReportingEligible(page)" :text="$t('dash.overview.actions.reporting')" position="top">
              <button class="ov-icon-btn" @click="$emit('open-reporting', page)"><i class="material-icons">insert_chart</i></button>
            </Tooltip>
            <Tooltip v-if="inboxCount(page)" :text="$t('dash.overview.pages.inbox', { count: inboxCount(page) })" position="top">
              <button class="ov-icon-btn ov-icon-btn--badge" @click="$emit('open-inbox', page)">
                <i class="material-icons">question_answer</i>
                <span class="ov-icon-badge">{{ inboxCount(page) > 99 ? '99+' : inboxCount(page) }}</span>
              </button>
            </Tooltip>
          </template>
        </div>
      </li>
    </ul>

    <!-- Reconnexion native : formulaire de la section Pages -->
    <ConciergerieForm
      v-if="nativePage"
      :page="nativePage"
      :active-conciergerie="nativePage"
      @getPagesEvent="onNativeReconnected"
      @closeActiveConciergerie="nativePage = null"
    />
  </div>
</template>

<script>
import PageImageAvatar from '../PageImageAvatar.vue'
import Tooltip from '../tooltip/tooltipInit.vue'
import { isReportingEligible } from '@/utils/reportingEligibility'
import ConciergerieForm from '../overlay/ConciergerieForm.vue'

const DAY = 24 * 3600 * 1000
// Réseaux gérés par le formulaire de connexion native (ConciergerieForm)
const NATIVE_FORM_NETWORKS = ['instagram', 'linkedin', 'tiktok', 'facebook', 'twitter']

export default {
  name: 'OverviewPages',

  components: { PageImageAvatar, Tooltip, ConciergerieForm },

  props: {
    pages: { type: Array, default: () => [] },
    perPage: { type: Array, default: null },
    canEdit: { type: Boolean, default: false },
    reportingAllowed: { type: Boolean, default: true },
    canReconnectInline: { type: Function, required: true },
    // id de page -> nombre d'événements inbox en attente
    inboxCounts: { type: Object, default: () => ({}) },
    // id de page -> date ISO de la prochaine publication planifiée (bloc À venir)
    nextScheduled: { type: Object, default: () => ({}) }
  },

  data() {
    return {
      // Page dont on refait la connexion native (formulaire ouvert)
      nativePage: null
    }
  },

  computed: {
    statsLoaded() {
      return !!this.perPage
    },

    statsById() {
      return (this.perPage || []).reduce((acc, p) => ({ ...acc, [p.id]: p }), {})
    },

    /**
     * Meilleures pages en premier : score = meilleures stats + publication récente + publication planifiée bientôt
     * (chaque critère ramené entre 0 et 1) ; les pages sans connexion utilisable restent en fin de liste
     */
    sortedPages() {
      const now = Date.now()
      const maxEngagement = Math.max(1, ...this.pages.map(p => this.stat(p).engagement || 0))
      const score = p => {
        const s = this.stat(p)
        const stats = (s.engagement || 0) / maxEngagement
        const lastPosted = s.last_posted_at ? (now - new Date(s.last_posted_at).getTime()) / DAY : null
        const recent = lastPosted === null ? 0 : Math.max(0, 1 - lastPosted / 30)
        const next = this.nextScheduled[p.id] ? (new Date(this.nextScheduled[p.id]).getTime() - now) / DAY : null
        const upcoming = next === null ? 0 : Math.max(0, 1 - next / 7)
        return stats + recent + upcoming
      }
      return [...this.pages].sort((a, b) =>
        Number(this.isDisconnected(a)) - Number(this.isDisconnected(b)) ||
        score(b) - score(a) ||
        (this.stat(b).page_fans || 0) - (this.stat(a).page_fans || 0)
      )
    }
  },

  methods: {
    stat(page) {
      return this.statsById[page.id] || {}
    },

    // bot_error contient soit un message, soit une clé i18n (bot désactivé)
    pageError(page) {
      const err = page && page.bot_error
      if (!err) return ''
      return this.$te(err) ? this.$t(err) : String(err)
    },

    isReportingEligible,

    // Aucune connexion utilisable (ni API, ni natif) : seule la reconnexion est proposée
    isDisconnected(page) {
      const api = this.apiState(page)
      const apiOk = !!api && api !== 'disconnected' && api !== 'unknown'
      const nativeOk = this.nativeState(page) === 'connected'
      if (!this.nativeOf(page)) return Number(page.bot_status) === 0 && !this.nativeOnly(page)
      return !apiOk && !nativeOk
    },

    apiNeedsReconnect(page) {
      return ['disconnected', 'expiring'].includes(this.apiState(page))
    },

    inboxCount(page) {
      return this.inboxCounts[page.id] || 0
    },

    needsReconnect(page) {
      return this.apiNeedsReconnect(page) || ['disconnected', 'action_required'].includes(this.nativeState(page))
    },

    // Connexion native à refaire, sur un réseau que le formulaire de connexion native sait gérer
    nativeNeedsReconnect(page) {
      return NATIVE_FORM_NETWORKS.includes(page.network) && ['disconnected', 'action_required'].includes(this.nativeState(page))
    },

    // Connexion faite (ou en attente de double authentification) : données rechargées, le formulaire reste ouvert jusqu'à sa fermeture
    onNativeReconnected() {
      this.$emit('native-reconnected', this.nativePage)
    },

    // État natif renvoyé par l'engine (null tant que les stats ne sont pas chargées)
    nativeOf(page) {
      return this.stat(page).native || null
    },

    nativeOnly(page) {
      const native = this.nativeOf(page)
      return !!((native && native.only) || (page.story_config && page.story_config.onlyConciergerie))
    },

    apiState(page) {
      if (this.nativeOnly(page)) return null
      switch (Number(page.bot_status)) {
        case 1: return 'connected'
        case 2: return 'expiring'
        case 0: return 'disconnected'
        case 3: return 'deactivated'
        default: return 'unknown'
      }
    },

    nativeState(page) {
      const native = this.nativeOf(page)
      return native && !['never', 'unsupported'].includes(native.state) ? native.state : null
    },

    // Badges API / Natif (conciergerie) avec leur état
    connections(page) {
      const level = { connected: 'ok', expiring: 'warning', locked: 'warning', action_required: 'warning', disconnected: 'danger', deactivated: 'muted', unknown: 'muted' }
      const list = []
      const api = this.apiState(page)
      if (api) {
        const detail = this.pageError(page)
        const state = this.$t(`dash.overview.conn.state.${api}`)
        list.push({ type: 'api', label: this.$t('dash.overview.conn.api'), level: level[api],
          tooltip: `${this.$t('dash.overview.conn.api_long')} : ${state}${detail ? ` — ${detail}` : ''}` })
      }
      const native = this.nativeState(page)
      if (native) {
        const until = native === 'locked' && this.nativeOf(page).locked_until ? ` (${new Date(this.nativeOf(page).locked_until).toLocaleString()})` : ''
        list.push({ type: 'native', label: this.$t('dash.overview.conn.native'), level: level[native],
          tooltip: `${this.$t('dash.overview.conn.native_long')} : ${this.$t(`dash.overview.conn.state.${native}`)}${until}` })
      }
      return list
    },

    compact(v) {
      const abs = Math.abs(v)
      if (abs >= 1000000) return (v / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (abs >= 1000) return (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return v.toLocaleString()
    }
  }
}
</script>

<style scoped>
.pages-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  max-height: 560px;
  overflow-y: auto;
}
.page-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 10px;
}
.page-row + .page-row {
  margin-top: 2px;
}
.page-row:hover {
  background: #f6f1fa;
}
.page-avatar {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 36px;
}
.page-main {
  flex: 1;
  min-width: 0;
}
.page-name {
  font-size: 13px;
  font-weight: 600;
  color: #121826;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-bottom: 4px;
}
.conn-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.conn-badge,
.health-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 20px;
  padding: 2px 8px;
  background: #f1f1f4;
  color: #6b7280;
}
.conn-badge .dot,
.health-pill .dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
}
.conn-badge--ok { background: #e6f9f0; color: #1a9e5e; }
.conn-badge--danger { background: #ffdede; color: #e04848; }
.conn-badge--warning { background: #ffe6d0; color: #d96a0c; }
.page-stats {
  display: flex;
  gap: 18px;
}
.page-stat {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  min-width: 50px;
  font-size: 14px;
  color: #121826;
}
.page-stat strong {
  font-weight: 700;
}
.page-stat small {
  font-size: 10px;
  color: #9ba3b7;
}
.page-stat .up { font-size: 11px; font-weight: 700; color: #1a9e5e; }
.page-stat .down { font-size: 11px; font-weight: 700; color: #d93025; }
.page-actions {
  display: flex;
  gap: 6px;
}
@media (max-width: 700px) {
  .page-row { flex-wrap: wrap; }
  .page-stats { width: 100%; padding-left: 52px; }
}
</style>
